"use strict";
/*
  based on: https://github.com/rzeigler/waveguide-streams/blob/master/src/step.ts
  credits to original author
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
exports.__esModule = true;
/* tested in wave */
/* istanbul ignore file */
var fp_ts_1 = require("fp-ts");
var step_1 = require("./step");
var T = __importStar(require("../effect"));
var effect_1 = require("../effect");
/**
 * Step a sink repeatedly.
 * If the sink completes before consuming all of the input, then the done state will include the ops leftovers
 * and anything left in the array
 * @param sink
 * @param s
 * @param multi
 */
function stepMany(sink, s, multi) {
    function go(current, i) {
        if (i === multi.length) {
            return T.pure(current);
        }
        else if (step_1.isSinkDone(current)) {
            return T.pure(step_1.sinkDone(current.state, current.leftover.concat(multi.slice(i))));
        }
        else {
            return effect_1.effect.chain(sink.step(current.state, multi[i]), function (next) { return go(next, i + 1); });
        }
    }
    return go(step_1.sinkCont(s), 0);
}
exports.stepMany = stepMany;
function liftPureSink(sink) {
    return {
        initial: T.pure(sink.initial),
        step: function (state, next) { return T.pure(sink.step(state, next)); },
        extract: fp_ts_1["function"].flow(sink.extract, T.pure)
    };
}
exports.liftPureSink = liftPureSink;
function collectArraySink() {
    var initial = T.pure(step_1.sinkCont([]));
    function step(state, next) {
        return T.pure(step_1.sinkCont(__spreadArrays(state, [next])));
    }
    return { initial: initial, extract: T.pure, step: step };
}
exports.collectArraySink = collectArraySink;
function drainSink() {
    var initial = T.pure(step_1.sinkCont(undefined));
    var extract = fp_ts_1["function"].constant(T.unit);
    function step(_state, _next) {
        return T.pure(step_1.sinkCont(undefined));
    }
    return { initial: initial, extract: extract, step: step };
}
exports.drainSink = drainSink;
/**
 * A sink that consumes no input to produce a constant b
 * @param b
 */
function constSink(b) {
    var initial = T.pure(step_1.sinkDone(undefined, []));
    var extract = fp_ts_1["function"].constant(T.pure(b));
    function step(_state, _next) {
        return T.pure(step_1.sinkDone(undefined, []));
    }
    return { initial: initial, extract: extract, step: step };
}
exports.constSink = constSink;
/**
 * A sink that produces the head element of a stream (if any elements are emitted)
 */
function headSink() {
    var initial = T.pure(step_1.sinkCont(fp_ts_1.option.none));
    function step(_state, next) {
        return T.pure(step_1.sinkDone(fp_ts_1.option.some(next), []));
    }
    return { initial: initial, extract: T.pure, step: step };
}
exports.headSink = headSink;
/**
 * A sink that produces the last element of a stream (if any elements are emitted)
 */
function lastSink() {
    var initial = T.pure(step_1.sinkCont(fp_ts_1.option.none));
    function step(_state, next) {
        return T.pure(step_1.sinkCont(fp_ts_1.option.some(next)));
    }
    return { initial: initial, extract: T.pure, step: step };
}
exports.lastSink = lastSink;
/**
 * A sink that evalutes an action for every element of a sink and produces no value
 * @param f
 */
function evalSink(f) {
    var initial = T.pure(step_1.sinkCont(undefined));
    function step(_state, next) {
        return fp_ts_1.pipeable.pipe(f(next), T.apSecond(T.pure(step_1.sinkCont(_state))));
    }
    var extract = fp_ts_1["function"].constant(T.unit);
    return { initial: initial, extract: extract, step: step };
}
exports.evalSink = evalSink;
/**
 * A sink that consumes elements for which a predicate does not hold.
 *
 * Returns the first element for which the predicate did hold if such an element is found.
 * @param f
 */
function drainWhileSink(f) {
    var initial = step_1.sinkCont(fp_ts_1.option.none);
    function step(_state, a) {
        return f(a) ? step_1.sinkCont(fp_ts_1.option.none) : step_1.sinkDone(fp_ts_1.option.some(a), []);
    }
    var extract = fp_ts_1["function"].identity;
    return liftPureSink({ initial: initial, extract: extract, step: step });
}
exports.drainWhileSink = drainWhileSink;
/**
 * A sink that offers elements into a concurrent queue
 *
 * @param queue
 */
function queueSink(queue) {
    var initial = T.pure(step_1.sinkCont(undefined));
    function step(_state, a) {
        return T.as(queue.offer(a), step_1.sinkCont(undefined));
    }
    var extract = fp_ts_1["function"].constant(T.unit);
    return { initial: initial, extract: extract, step: step };
}
exports.queueSink = queueSink;
/**
 * A sink that offers elements into a queue after wrapping them in an option.
 *
 * The sink will offer one final none into the queue when the stream terminates
 * @param queue
 */
function queueOptionSink(queue) {
    var initial = T.pure(step_1.sinkCont(undefined));
    function step(_state, a) {
        return T.as(queue.offer(fp_ts_1.option.some(a)), step_1.sinkCont(undefined));
    }
    var extract = fp_ts_1["function"].constant(queue.offer(fp_ts_1.option.none));
    return { initial: initial, extract: extract, step: step };
}
exports.queueOptionSink = queueOptionSink;
/**
 * Map the output value of a sink
 * @param sink
 * @param f
 */
function map(sink, f) {
    return __assign(__assign({}, sink), { extract: fp_ts_1["function"].flow(sink.extract, T.map(f)) });
}
exports.map = map;
